/**
 * Written once, never overwritten: a last-touch model would relabel every AI
 * arrival as "canvas". In `localStorage` because sign-out clears PostHog's
 * super properties.
 */

import {
  captureAnalytics,
  refreshAttributionProperties,
} from "./analytics";
import { preserveLegacyFreeAllowance } from "./free-allowance-promise";

const KEY = "twelve-volt:first-touch";

export type ArrivalSurface = "share" | "public" | "owner" | "project" | "cold";

export type FirstValueAction = "ai_system" | "ai_component" | "canvas";

/** What the browser knew at the moment of arrival. PostHog captures the same
 * facts on the pageview, but its copy lives in PostHog persistence, which
 * `posthog.reset()` wipes on sign-out — the reason this record exists at all. */
export type ArrivalContext = {
  referrer: string;
  landingPath: string;
  search: string;
};

/** Campaign identifiers worth keeping from the first URL. UTMs plus the ad
 * click ids that survive when a platform strips the referrer. */
const CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
] as const;

type FirstTouch = {
  surface: ArrivalSurface;
  at: string;
  firstValue: FirstValueAction | null;
  firstValueAt: string | null;
  /** `"$direct"` when the browser sent none (PostHog's convention). Absent on
   * records written before attribution capture existed. */
  referrer?: string;
  referringDomain?: string;
  landingPath?: string;
  utm?: Record<string, string>;
};

export function readArrivalContext(): ArrivalContext | null {
  if (typeof document === "undefined" || typeof location === "undefined") {
    return null;
  }
  return {
    referrer: document.referrer,
    landingPath: location.pathname,
    search: location.search,
  };
}

function attributionFor(ctx: ArrivalContext | null): Partial<FirstTouch> {
  if (!ctx) return {};
  let referringDomain = "$direct";
  if (ctx.referrer) {
    try {
      referringDomain = new URL(ctx.referrer).hostname;
    } catch {
      referringDomain = ctx.referrer;
    }
  }
  const utm: Record<string, string> = {};
  const params = new URLSearchParams(ctx.search);
  for (const key of CAMPAIGN_PARAMS) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }
  return {
    referrer: ctx.referrer || "$direct",
    referringDomain,
    landingPath: ctx.landingPath,
    ...(Object.keys(utm).length > 0 ? { utm } : {}),
  };
}

function propertiesFor(a: FirstTouch | null): Record<string, unknown> {
  if (!a) return {};
  const props: Record<string, unknown> = {
    arrival_surface: a.surface,
    arrival_at: a.at,
    first_value_action: a.firstValue,
    first_value_at: a.firstValueAt,
    first_value_was_ai:
      a.firstValue === "ai_system" || a.firstValue === "ai_component",
  };
  if (a.referrer) props.arrival_referrer = a.referrer;
  if (a.referringDomain) props.arrival_referring_domain = a.referringDomain;
  if (a.landingPath) props.arrival_landing_path = a.landingPath;
  if (a.utm) {
    for (const [key, value] of Object.entries(a.utm)) {
      props[`arrival_${key}`] = value;
    }
  }
  return props;
}

function read(): FirstTouch | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FirstTouch>;
    return typeof parsed?.surface === "string" ? (parsed as FirstTouch) : null;
  } catch {
    return null;
  }
}

function write(value: FirstTouch): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

/** Whether an earlier boot already recorded an arrival in this browser. Read
 * it before `recordArrival`, which makes it true. */
export function hasRecordedArrival(): boolean {
  return read() !== null;
}

/** Record how this person arrived. Idempotent: the first touch wins — a later
 * visit through an ad link must not relabel an organic first arrival. */
export function recordArrival(
  surface: ArrivalSurface,
  now: () => Date = () => new Date(),
  context: ArrivalContext | null = readArrivalContext(),
): void {
  if (read()) return;
  preserveLegacyFreeAllowance();
  write({
    surface,
    at: now().toISOString(),
    firstValue: null,
    firstValueAt: null,
    ...attributionFor(context),
  });
}

/** Only the first call lands, so callers must not guard it themselves. */
export function recordFirstValue(
  action: FirstValueAction,
  now: () => Date = () => new Date(),
): void {
  const reachedAt = now();
  preserveLegacyFreeAllowance();
  // A deep link into a generation can race boot; seed a cold arrival rather
  // than drop the signal.
  const current: FirstTouch = read() ?? {
    surface: "cold",
    at: reachedAt.toISOString(),
    firstValue: null,
    firstValueAt: null,
    ...attributionFor(readArrivalContext()),
  };
  if (current.firstValue) return;
  const next: FirstTouch = {
    ...current,
    firstValue: action,
    firstValueAt: reachedAt.toISOString(),
  };
  write(next);

  // Storage is best-effort. Keep this session's analytics correct even when
  // private browsing or quota limits prevent the durable write.
  const properties = propertiesFor(next);
  refreshAttributionProperties(properties);
  const elapsedMs = reachedAt.getTime() - Date.parse(current.at);
  captureAnalytics("first_value_reached", {
    first_value_action: action,
    first_value_was_ai:
      action === "ai_system" || action === "ai_component",
    arrival_surface: current.surface,
    seconds_to_first_value: Number.isFinite(elapsedMs)
      ? Math.max(0, elapsedMs / 1_000)
      : 0,
  });
}

/** Set as person properties too — revenue reporting cannot read super properties. */
export function firstTouchProperties(): Record<string, unknown> {
  return propertiesFor(read());
}

/** Order matters: a share token on a public path is a share arrival. */
export function arrivalSurfaceFor(url: {
  shareToken: string | null;
  publicSlug: string | null;
  publicProjectId: string | null;
  ownerSlug: string | null;
  projectId: string | null;
}): ArrivalSurface {
  if (url.shareToken) return "share";
  if (url.publicSlug || url.publicProjectId) return "public";
  if (url.ownerSlug) return "owner";
  if (url.projectId) return "project";
  return "cold";
}
